import { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { BILLING_STATUS } from "../../../../config/constants";
import { FormControlLabel, Checkbox, IconButton, Typography, Box, Grid, Button } from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { useStyles } from "@webapps/styles";
import { useQuery, useAuth } from "@webapps/reactjs-tech-components";
import { getBillingsYearlyHistory } from "@webapps/business";

const MONTHS = ["JAN", "FEV", "MARS", "AVR", "MAI", "JUIN", "JUIL", "AOUT", "SEPT", "OCT", "NOV", "DEC"];

const AccountingChart = ({ state }) => {
  const { global, classes } = useStyles(styles);
  const { idUser } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [year, setYear] = useState(currentYear);

  // Initialize visibleSeries with all statuses enabled
  const defaultVisibleSeries = useMemo(() =>
    BILLING_STATUS.reduce((acc, status) => {
      acc[status.label] = true;
      return acc;
    }, {}),
    []
  );

  // Map state.value to visibleSeries configuration
  const stateVisibleSeries = useMemo(() => {
    const stateMap = {
      'A_VENIR': ['À venir'],
      'CREATED': ['À envoyer'],
      'TRANSMIS': ['Transmises'],
      'EN_COURS_REGLEMENT': ['En cours de réglement'],
      'REGLE': ['Réglé'],
      'NON_ABOUTIES': ['Refusé', 'Annulé', 'Bloqué', 'Relance']
    };

    if (!state?.value || !stateMap[state.value]) {
      return defaultVisibleSeries;
    }

    return BILLING_STATUS.reduce((acc, status) => {
      acc[status.label] = stateMap[state.value].includes(status.label);
      return acc;
    }, {});
  }, [state?.value, defaultVisibleSeries]);

  // Merge default and state-driven visibility, allowing manual toggles
  const [visibleSeries, setVisibleSeries] = useState(defaultVisibleSeries);

  // Update visibleSeries when state changes, but preserve manual toggles
  useMemo(() => {
    setVisibleSeries(stateVisibleSeries);
  }, [stateVisibleSeries]);

  const { data: history = [], isLoading } = useQuery(getBillingsYearlyHistory, {
    key: ['billingHistory', year],
    variables: { year, id: idUser },
  });

  const { seriesData, maxY } = useMemo(() => {
    const series = history.map((item) => {
      const color = BILLING_STATUS.find(billing => billing.value === item.status)?.colorValue;
      const name = BILLING_STATUS.find(billing => billing.value === item.status)?.label;
      return {
        name: name,
        data: item.axis.map(point => ({ x: point.x, y: point.y })),
        color: color
      };
    });

    const allYs = history.flatMap(item => item.axis.map(point => point.y));
    const max = allYs.length > 0 ? Math.max(...allYs) : 0;

    return { seriesData: series, maxY: Math.ceil(max * 1) };
  }, [history]);

  const filteredSeries = seriesData.filter(series => visibleSeries[series.name]);

  const handlePreviousYear = () => setYear(year - 1);
  const handleNextYear = () => setYear(year + 1);

  const handleSeriesToggle = (value) => {
    setVisibleSeries(prev => ({
      ...prev,
      [value]: !prev[value]
    }));
  };

  const chartOptions = {
    chart: {
      height: 200,
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Roboto, sans-serif'
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    grid: {
      borderColor: '#e7e7e7',
      row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 },
      padding: { left: 0, right: 0 }
    },
    markers: { size: 5 },
    xaxis: {
      type: 'numeric',
      min: 0,
      max: 11,
      tickAmount: 11,
      labels: {
        show: true,
        formatter: (val) => MONTHS[Math.round(val)],
        style: {
          fontSize: '14px',
          fontFamily: 'Roboto, sans-serif',
          color: '#5D75A8',
          fontWeight: (value, seriesIndex, dataPointIndex) =>
            dataPointIndex === currentMonth && year === currentYear ? 700 : 400
        }
      },
      axisBorder: { show: true },
      axisTicks: { show: true }
    },
    yaxis: {
      min: 0,
      max: maxY,
      tickAmount: 6,
      labels: {
        formatter: (val) => val.toFixed(0),
        style: {
          colors: '#5D75A8',
          fontSize: '14px',
          fontFamily: 'Roboto, sans-serif',
          marginRight: '10px',
          padding: '10px'
        },
        offsetX: -10
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5,
      fontFamily: 'Roboto, sans-serif'
    },
    tooltip: {
      x: { formatter: (val) => MONTHS[val] + ' ' + year }
    }
  };

  return (
    <div className={classes.container}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box style={{ gap: '10px' }} display="flex" alignItems="center">
          <IconButton className={classes.arrowButton} onClick={handlePreviousYear} disabled={isLoading}>
            <ChevronLeft />
          </IconButton>
          <div style={{ width: 'max-content' }} className={global.title}>Année {year}</div>
          <IconButton
            className={classes.arrowButton}
            onClick={handleNextYear}
            disabled={isLoading}
          >
            <ChevronRight />
          </IconButton>
        </Box>
        <Grid container spacing={2} style={{ justifyContent: 'flex-end' }}>
          {BILLING_STATUS.map((status) => (
            <Grid item key={status.value}>
              <FormControlLabel
                classes={{ label: classes.checkboxLabel }}
                control={
                  <Checkbox
                    className={classes.checkbox}
                    checked={visibleSeries[status.label] || false}
                    onChange={() => handleSeriesToggle(status.label)}
                    style={{
                      color: status.colorValue,
                      padding: '0px',
                      width: '24px',
                      height: '24px'
                    }}
                  />
                }
                label={status.label}
                style={{ marginRight: '12px' }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
      <div id="chart" style={{ height: '200px' }}>
        <ReactApexChart
          options={chartOptions}
          series={filteredSeries}
          type="line"
          height={200}
        />
      </div>
    </div>
  );
};

const styles = ({ spacing }) => ({
  container: {
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: '8px',
  },
  arrowButton: {
    color: '#5D75A8',
    backgroundColor: '#F3F7FF',
    '@media only screen and (max-width:1920px)': { fontSize: '13.500px' },
    '@media only screen and (max-width:1600px)': { fontSize: '13.5000px' },
    border: '1px solid #C0D9FF',
    borderRadius: '400px',
    fontWeight: 600,
    padding: '3px !important'
  },
  checkbox: {
    marginRight: '6px'
  },
  checkboxLabel: {
    color: '#152652',
    fontSize: '14px',
    fontWeight: 500
  }
});

export default AccountingChart;